import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslations } from '@libs/translations';
import { SUPPORT_LANGS } from '@constants/langs';
import { addTrailingSlash, getLocale } from '@libs';
import { HOSTNAME, GTM_ID } from '@constants';

const OG_LOCALE_MAP = {
  ru: 'ru_RU',
  en: 'en_US',
  de: 'de_DE',
  es: 'es_ES',
};

export const MainHead = ({
  title = '',
  description = '',
  keywords = '',
  meta = null,
  alternateLinks,
  ogImage = '',
  ogType = 'website',
}) => {
  const { locale, asPath } = useRouter();
  const translations = useTranslations();

  // используем url без get-параметров, тк у нас пока нет таких параметров, которые меняют контент страницы
  const urlWithoutParams = asPath.split('?')[0];
  const isHomePage = urlWithoutParams === '/';
  const urlHasDots = ~urlWithoutParams.indexOf('.');
  const canonicalLocale = getLocale(locale);

  const getAlternateLinks = () => {
    return SUPPORT_LANGS.map(({ hrefLang, value }) => {
      let href = '';

      if (!isHomePage) {
        href = value + addTrailingSlash(urlWithoutParams, urlHasDots);
      } else {
        // для урла вида http://localhost:3000/ или http://localhost:3000/en/
        href = value + '/';
      }

      return {
        href: 'https://' + HOSTNAME + '/' + href,
        hrefLang,
      };
    });
  };
  const canonical = 'https://' + HOSTNAME + `/${canonicalLocale}` + addTrailingSlash(urlWithoutParams, urlHasDots);
  // если передан массив alternateLinks то берем из него - например страница записи блога
  const alternate = (alternateLinks || []).length > 0 ? alternateLinks : getAlternateLinks();
  const ogDescription = description || translations.t('site.description.main');
  const ogImageUrl = ogImage ? (ogImage.startsWith('http') ? ogImage : 'https://' + HOSTNAME + ogImage) : '';
  const ogLocale = OG_LOCALE_MAP[canonicalLocale] || OG_LOCALE_MAP.en;

  return (
    <Head>
      <title>{title}</title>
      <link rel="icon" type="image/x-icon" href="/favicon.ico?v=2" />
      <link rel="shortcut icon" type="image/svg+xml" href="/static/favicons/favicon.svg?v=2" />
      <link rel="apple-touch-icon" sizes="180x180" href="/static/favicons/favicon-180x180.png?v=2" />
      <link href="/styles.css" rel="stylesheet" />
      <script
        type="text/javascript"
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){
          w[l]=w[l]||[];
          w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
          })(window,document,'script','dataLayer', '${GTM_ID}');`,
        }}
      />
      <meta charSet="UTF-8" />
      <meta name="Author" content="Kelnik" />
      <meta name="description" content={description || translations.t('site.description.main')} />
      <meta name="Keywords" content={keywords || translations.t('site.keywords.main')} />
      <meta name="robots" content="noyaca" />
      <meta name="format-detection" content="telephone=no" />
      {meta && meta()}
      <meta name="p:domain_verify" content="b9b574221b416aff409594749aad1dfd" /> {/* pinterest */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content="Planoplan" />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={ogDescription} />
      {ogImageUrl && <meta property="og:image" content={ogImageUrl} />}
      <meta name="twitter:card" content={ogImageUrl ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={ogDescription} />
      {ogImageUrl && <meta name="twitter:image" content={ogImageUrl} />}
      <link rel="canonical" href={canonical} />
      {alternate.map(({ href, hrefLang }) => (
        <link key={href} rel="alternate" href={href} hrefLang={hrefLang} />
      ))}
    </Head>
  );
};
